import {
  InfiniteData,
  useMutation,
  useQueryClient
} from '@tanstack/react-query'

import type {
  AddRequestCommentDTO,
  RequestCommentEntity
} from '../lib/request-comments.types'
import { RequestsService } from '../lib/requests.service'
import { REQUEST_COMMENTS_QUERY_KEY } from './useGetRequestCommentsList'

export const useAddRequestComment = (requestId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (formData: AddRequestCommentDTO) =>
      RequestsService.addRequestComment(formData),
    onSuccess: async newComment => {
      await queryClient.cancelQueries({
        queryKey: [REQUEST_COMMENTS_QUERY_KEY, requestId]
      })

      const previousData = queryClient.getQueryData([
        REQUEST_COMMENTS_QUERY_KEY,
        requestId
      ])

      queryClient.setQueryData<
        InfiniteData<
          {
            response: RequestCommentEntity[]
            page: number | undefined
          },
          number
        >
      >([REQUEST_COMMENTS_QUERY_KEY, requestId], old => {
        const updatedResponse = [
          ...(old?.pages[0]?.response || []),
          {
            id: requestId,
            message: newComment.data?.message,
            file: newComment.data?.file,
            createdAt: new Date(),
            sentByClient: newComment.data?.sentByClient ?? true,
            isRead: newComment.data?.isRead ?? false,
            authorName: newComment.data?.authorName ?? null
          }
        ]
        const firstPage = {
          response: updatedResponse,
          page: 1
        }

        return {
          pages: [firstPage, ...(old?.pages.slice(1) || [])],
          pageParams: [...(old?.pageParams || [1])]
        }
      })

      return {
        previousData
      }
    },
    onError: () => {
      queryClient.setQueryData<RequestCommentEntity[]>(
        [REQUEST_COMMENTS_QUERY_KEY, requestId],
        old => old || []
      )
    }
  })
}
